import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { categoryService, tagService } from "../shared/api";
import { companyService } from "../shared/api/companyService";
import { contactService } from "../shared/api/contactService";
import { CategoryIndexResource } from "../shared/types";
import { CategoryCreateDrawer, CategoryUpdateDrawer } from "./CategoryDrawer";

export const CategoryList: FC = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const canCreate = useHasPermission<PermissionName>(["category.create"]);
	const canUpdate = useHasPermission<PermissionName>(["category.update"]);

	const crudOperations = useLaravelCrudEntity(
		categoryService,
		{
			entityName: t("Category"),
			messages: {
				create: t("Category was successfully created."),
				update: t("Category was successfully updated."),
				delete: t("Category was successfully deleted."),
			},
		},
		[tagService.indexKey, companyService.indexKey, contactService.indexKey, categoryService.optionsKey],
	);

	const dataListColumns: IDrawerDataListColumn<CategoryIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit category")}
							label={t("Edit category")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={async () => {
								await crudOperations.deleteEntity(id);
								await queryClient.resetQueries({
									queryKey: tagService.indexKey,
								});
								await queryClient.resetQueries({
									queryKey: companyService.indexKey,
								});
								await queryClient.resetQueries({
									queryKey: contactService.indexKey,
								});
							}}
							title={t("Delete category")}
							label={t("Delete category")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this category?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[crudOperations, t, queryClient],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(categoryService.listKey, categoryService.index)}
			renderCreateDrawer={(drawerProps) => (
				<CategoryCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<CategoryUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			translations={{
				title: t("Categories"),
				empty: t("No categories available"),
			}}
			initialSearch={{ search: "", searchFields: [{ label: t("Name"), name: "category_translations.name" }] }}
		/>
	);
};
